import { formatDate } from "../../../../utils/format";
import {
  Clock,
  Globe,
  Shield,
  ScanSearch,
  Flame,
  Activity,
  Hash,
  Target,
  Eye,
  Sparkles,
} from "lucide-react";
import { DETECTION_ICONS } from "../../../../icons/detectionLucideIcons";
import {
  PRIMARY_COLOR,
  TEXT_PRIMARY,
  BLOCKED_STATUS_FG,
  isBlockedAction,
  drawerSeverityPalette,
} from "./wafEventDrawerShared";

/** Canonical layerd-v2-bm-events fields — drawer reads only these keys. */
export const BOT_EVENT_KEYS = [
  "ts",
  "request_id",
  "schema_version",
  "waf_version",
  "bm_engine_version",
  "site_id",
  "host",
  "method",
  "uri_path",
  "uri_query_hash",
  "status",
  "request_bytes",
  "response_bytes",
  "duration_us",
  "client_ip",
  "ad_uid",
  "bm_uid",
  "fingerprint",
  "ja3",
  "ja4",
  "tls_version",
  "cipher_suite",
  "alpn",
  "http_version",
  "country",
  "city",
  "region",
  "timezone",
  "asn",
  "asn_org",
  "bot_score",
  "bot_type",
  "fired_detectors",
  "aggregator_version",
  "fp_verdict",
  "fp_confidence",
  "ml_prob",
  "ml_model_version",
  "shadow_score",
  "shadow_type",
  "action",
  "action_reason",
  "action_intended",
  "bm_extra",
  "ja4h",
  "header_order_hash",
  "source_port",
  "dest_ip",
  "dest_port",
  "user_agent",
  "referer",
  "accept_language",
  "uri_query",
  "ip_reputation",
  "source_table",
  "id",
  "src_ip",
  "host_name",
  "uri",
  "ua",
  "resStatus",
  "ja3_hash",
  "attack_type",
  "waf_type",
  "bot_rule_id",
  "bot_rule_msg",
];

const KEY_ALIASES = {
  ts: ["ts", "timestamp"],
  uri_path: ["uri_path", "uri"],
  uri: ["uri", "uri_path"],
  client_ip: ["client_ip", "src_ip"],
  src_ip: ["src_ip", "client_ip"],
  host: ["host", "host_name"],
  host_name: ["host_name", "host"],
  status: ["status", "resStatus"],
  resStatus: ["resStatus", "status"],
  ja3: ["ja3", "ja3_hash"],
  ja3_hash: ["ja3_hash", "ja3"],
};

/**
 * Normalize detector names for Bot Events table/filters/drawers.
 * Primary: fired_detectors as Array, JSON array string, or Python-ish list.
 * Fallback (first non-empty): action_reason / bot_rule_msg / bot_name (+ raw.*).
 * Does not CSV-split freeform action_reason text (commas stay intact).
 */
export function getBotEventDetectors(event) {
  if (!event) return [];

  const isPlaceholder = (text) => {
    const t = text.toLowerCase();
    return (
      !text
      || text === "—"
      || text === "-"
      || t === "none"
      || t === "n/a"
      || t === "null"
      || t === "undefined"
      || t === "[object object]"
    );
  };

  const pushUnique = (out, raw) => {
    if (raw != null && typeof raw === "object") return;
    const text = String(raw ?? "").trim();
    if (isPlaceholder(text)) return;
    if (!out.includes(text)) out.push(text);
  };

  const fromArrayLike = (value) => {
    const out = [];
    if (value == null || value === "") return out;
    if (Array.isArray(value)) {
      value.forEach((item) => pushUnique(out, item));
      return out;
    }
    if (typeof value !== "string") {
      pushUnique(out, value);
      return out;
    }
    const trimmed = value.trim();
    if (!trimmed || isPlaceholder(trimmed)) return out;

    if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) return fromArrayLike(parsed);
      } catch (_err) {
        const inner = trimmed.slice(1, -1).trim();
        if (inner) {
          inner.split(",").forEach((part) => {
            pushUnique(out, part.trim().replace(/^['"]|['"]$/g, ""));
          });
          return out;
        }
      }
    }

    // Safe multi-value separators only — never split on commas (action_reason prose).
    if (/[|;]/.test(trimmed)) {
      trimmed.split(/[|;]+/).forEach((part) => pushUnique(out, part));
      return out;
    }

    pushUnique(out, trimmed);
    return out;
  };

  const primary = fromArrayLike(event.fired_detectors ?? event.raw?.fired_detectors);
  if (primary.length) return primary;

  const fallbackSources = [
    event.action_reason,
    event.bot_rule_msg,
    event.bot_name,
    event.raw?.action_reason,
    event.raw?.bot_rule_msg,
    event.raw?.bot_name,
  ];

  for (const source of fallbackSources) {
    if (source == null || source === "") continue;
    const parsed = Array.isArray(source) || (typeof source === "string" && source.trim().startsWith("["))
      ? fromArrayLike(source)
      : (() => {
          const single = [];
          pushUnique(single, source);
          return single;
        })();
    if (parsed.length) return parsed;
  }

  return [];
}

export function getBotEventValue(event, key) {
  if (!event || !key) return undefined;
  const candidates = KEY_ALIASES[key] || [key];
  for (const candidate of candidates) {
    const value = event[candidate];
    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }
  return undefined;
}

/** Resolve bot event filter dropdown values from list API fields. */
export function getBotEventFilterValue(event, filterId) {
  if (!event) return undefined;
  switch (filterId) {
    case "id":
    case "request_id":
      return event.request_id || event.id;
    case "hostname":
      return event.host_name || event.host || event.hostname;
    case "src_ip":
      return event.src_ip || event.client_ip;
    case "uri":
      return event.uri || event.uri_path;
    case "resStatus":
    case "res_code":
    case "res_status":
      return event.status ?? event.resStatus ?? event.res_status ?? event.res_code;
    case "bot_type":
      return event.bot_type || event.attack_type;
    case "bot_name": {
      const detectors = getBotEventDetectors(event);
      return detectors.length ? detectors : undefined;
    }
    case "ja3_hash":
    case "ja3":
      return event.ja3 || event.ja3_hash;
    default:
      return getBotEventValue(event, filterId);
  }
}

export function pickBotEventPayload(event) {
  const out = {};
  BOT_EVENT_KEYS.forEach((key) => {
    const value = getBotEventValue(event, key);
    if (value !== undefined) {
      out[key] = value;
    }
  });
  return out;
}

function displayValue(value) {
  if (value === null || value === undefined) return null;
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) return value.length ? value.join(", ") : null;
  const text = String(value).trim();
  return text || null;
}

function formatTimestamp(value) {
  if (!value) return null;
  try {
    return formatDate(value);
  } catch (_err) {
    return displayValue(value);
  }
}

function formatBotAction(value) {
  if (!value) return null;
  const action = String(value).trim().toLowerCase();
  if (action === "block" || action === "blocked") return "Blocked";
  if (action === "challenge" || action === "challenged") return "Challenged";
  if (action === "allow" || action === "passed" || action === "detect") return "Detect";
  return String(value);
}

function getBotScoreClassification(score) {
  if (score === undefined || score === null || score === "") return null;
  const numeric = Number(score);
  if (!Number.isFinite(numeric)) return null;
  if (numeric >= 1 && numeric <= 20) return "Bad Bot";
  if (numeric >= 21 && numeric <= 30) return "Good Bot (Verified)";
  if (numeric >= 31 && numeric <= 100) return "Human";
  return null;
}

function formatBotScoreClassification(_value, event) {
  return getBotScoreClassification(getBotEventValue(event, "bot_score"));
}

function toAssessCells(event, specs, iconMap) {
  return specs
    .map(({ key, label, monospace, copyable, format }) => {
      const raw = getBotEventValue(event, key);
      const formatted = format ? format(raw, event) : displayValue(raw);
      if (!formatted) return null;

      let valueColor;
      if (key === "host_name") {
        valueColor = PRIMARY_COLOR;
      } else if (key === "action" || key === "action_intended") {
        valueColor = isBlockedAction(formatted) ? BLOCKED_STATUS_FG : TEXT_PRIMARY;
      } else if (key === "bot_score_classification") {
        valueColor = drawerSeverityPalette(String(formatted).toLowerCase()).fg;
      }

      return {
        key,
        label,
        value: formatted,
        icon: iconMap[key],
        valueColor,
        monospace,
        copyable,
      };
    })
    .filter(Boolean);
}

const DETECTION_SUMMARY_SPECS = [
  { key: "ts", label: "Timestamp", format: formatTimestamp },
  { key: "host_name", label: "Hostname" },
  { key: "bot_type", label: "Bot Type" },
  { key: "bot_score", label: "Bot Score" },
  {
    key: "bot_score_classification",
    label: "Bot Classification",
    format: formatBotScoreClassification,
  },
  { key: "attack_type", label: "Attack Type" },
  { key: "action", label: "Action", format: formatBotAction },
  { key: "action_intended", label: "Action Intended", format: formatBotAction },
  { key: "action_reason", label: "Action Reason" },
  { key: "bot_rule_msg", label: "Bot Rule Message" },
  { key: "fp_verdict", label: "FP Verdict" },
  { key: "fp_confidence", label: "FP Confidence" },
  { key: "ml_prob", label: "ML Probability" },
  { key: "shadow_score", label: "Shadow Score" },
  { key: "shadow_type", label: "Shadow Type" },
  { key: "fired_detectors", label: "Fired Detectors" },
];

const DETECTION_SUMMARY_ICONS = {
  ts: Clock,
  host_name: Globe,
  bot_type: ScanSearch,
  bot_score: Activity,
  bot_score_classification: Flame,
  attack_type: DETECTION_ICONS.detection,
  action: Shield,
  action_intended: Shield,
  action_reason: DETECTION_ICONS.info,
  bot_rule_msg: DETECTION_ICONS.clipboard,
  fp_verdict: ScanSearch,
  fp_confidence: Activity,
  ml_prob: DETECTION_ICONS.ml,
  shadow_score: Activity,
  shadow_type: Eye,
  fired_detectors: Target,
};

const REQUEST_ICONS = {
  method: DETECTION_ICONS.request,
  uri_path: DETECTION_ICONS.uri,
  uri_query: DETECTION_ICONS.uri,
  uri_query_hash: Hash,
  http_version: DETECTION_ICONS.responseStatus,
  user_agent: DETECTION_ICONS.header,
  referer: DETECTION_ICONS.uri,
  accept_language: DETECTION_ICONS.header,
  request_bytes: Activity,
  ua: Sparkles,
};

const RESPONSE_ICONS = {
  status: DETECTION_ICONS.responseStatus,
  response_bytes: Activity,
  duration_us: Clock,
};

const NETWORK_ICONS = {
  client_ip: DETECTION_ICONS.ip,
  src_ip: DETECTION_ICONS.ip,
  source_port: DETECTION_ICONS.ip,
  country: Globe,
  city: Globe,
  region: Globe,
  asn: DETECTION_ICONS.ipReputation,
  ip_reputation: DETECTION_ICONS.ipReputation,
};

const TLS_ICONS = {
  ja3: DETECTION_ICONS.ja3,
  ja3_hash: DETECTION_ICONS.ja3,
  ja4: DETECTION_ICONS.ja3,
  ja4h: DETECTION_ICONS.ja3,
  tls_version: Shield,
  cipher_suite: Hash,
  alpn: DETECTION_ICONS.responseStatus,
  fingerprint: DETECTION_ICONS.ja3,
  header_order_hash: Hash,
};

const METADATA_ICONS = {
  request_id: Hash,
  id: Hash,
  site_id: Globe,
  source_table: DETECTION_ICONS.cache,
  schema_version: DETECTION_ICONS.info,
  waf_version: DETECTION_ICONS.wafDetection,
  waf_type: DETECTION_ICONS.wafDetection,
  bm_engine_version: DETECTION_ICONS.wafDetection,
  aggregator_version: Activity,
  ml_model_version: DETECTION_ICONS.ml,
  ad_uid: Hash,
  bm_uid: Hash,
  bot_rule_id: Target,
  bm_extra: DETECTION_ICONS.info,
};

const REQUEST_SPECS = [
  { key: "method", label: "Method" },
  { key: "uri_path", label: "URI Path", monospace: true },
  { key: "uri_query", label: "URI Query", monospace: true },
  { key: "uri_query_hash", label: "URI Query Hash", monospace: true, copyable: true },
  { key: "http_version", label: "HTTP Version" },
  { key: "user_agent", label: "User Agent" },
  { key: "referer", label: "Referer" },
  { key: "accept_language", label: "Accept Language" },
  { key: "request_bytes", label: "Request Bytes" },
  { key: "ua", label: "UA (bot signal)" },
];

const RESPONSE_SPECS = [
  { key: "status", label: "Response Status" },
  { key: "response_bytes", label: "Response Bytes" },
  { key: "duration_us", label: "Duration (µs)" },
];

const NETWORK_SPECS = [
  { key: "client_ip", label: "Client IP", copyable: true },
  { key: "src_ip", label: "Source IP", copyable: true },
  { key: "source_port", label: "Source Port" },
  { key: "country", label: "Country" },
  { key: "city", label: "City" },
  { key: "region", label: "Region" },
  { key: "asn", label: "ASN" },
  { key: "ip_reputation", label: "IP Reputation" },
];

const TLS_SPECS = [
  { key: "ja3", label: "JA3", monospace: true, copyable: true },
  { key: "ja3_hash", label: "JA3 Hash", monospace: true, copyable: true },
  { key: "ja4", label: "JA4", monospace: true, copyable: true },
  { key: "ja4h", label: "JA4H", monospace: true, copyable: true },
  { key: "tls_version", label: "TLS Version" },
  { key: "cipher_suite", label: "Cipher Suite", monospace: true },
  { key: "alpn", label: "ALPN" },
  { key: "fingerprint", label: "Fingerprint", monospace: true, copyable: true },
  { key: "header_order_hash", label: "Header Order Hash", monospace: true, copyable: true },
];

const METADATA_SPECS = [
  { key: "request_id", label: "Request ID", monospace: true, copyable: true },
  { key: "id", label: "Event ID", monospace: true, copyable: true },
  { key: "site_id", label: "Site ID" },
  { key: "source_table", label: "Source Table", monospace: true },
  { key: "schema_version", label: "Schema Version" },
  { key: "waf_version", label: "WAF Version" },
  { key: "waf_type", label: "WAF Type" },
  { key: "bm_engine_version", label: "BM Engine Version" },
  { key: "aggregator_version", label: "Aggregator Version" },
  { key: "ml_model_version", label: "ML Model Version" },
  { key: "ad_uid", label: "AD UID", monospace: true, copyable: true },
  { key: "bm_uid", label: "BM UID", monospace: true, copyable: true },
  { key: "bot_rule_id", label: "Bot Rule ID", monospace: true, copyable: true },
  { key: "bm_extra", label: "BM Extra", monospace: true },
];

export function buildBotEventDrawerSections(event) {
  return {
    detectionAssessCells: toAssessCells(event, DETECTION_SUMMARY_SPECS, DETECTION_SUMMARY_ICONS),
    requestAssessCells: toAssessCells(event, REQUEST_SPECS, REQUEST_ICONS),
    responseAssessCells: toAssessCells(event, RESPONSE_SPECS, RESPONSE_ICONS),
    networkAssessCells: toAssessCells(event, NETWORK_SPECS, NETWORK_ICONS),
    tlsAssessCells: toAssessCells(event, TLS_SPECS, TLS_ICONS),
    metadataAssessCells: toAssessCells(event, METADATA_SPECS, METADATA_ICONS),
    payload: pickBotEventPayload(event),
    headerMethod: displayValue(getBotEventValue(event, "method")) || "GET",
    headerPath: displayValue(getBotEventValue(event, "uri_path") || getBotEventValue(event, "uri")) || "/",
    headerEventId: displayValue(getBotEventValue(event, "request_id") || getBotEventValue(event, "id")),
  };
}

export function resolveDrawerEventType(event_Type, event) {
  const normalizedProp = event_Type === "bot_management" ? "bot" : event_Type;
  if (normalizedProp === "bot" || normalizedProp === "firewall" || normalizedProp === "ratelimit") {
    return normalizedProp;
  }
  const wafType = event?.waf_type;
  if (wafType === "smart_parse" || wafType === "signature_waf") {
    return wafType;
  }
  return normalizedProp || "waf";
}
